import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/super-admin`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Cuentas que no reciben pagos de clientes (comisiones de Caura, cuentas propias
// de comercios): un crédito ahí sin orden no es un pago por conciliar.
export default function ExcludedAccountsModal({ onClose, setToast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/non-transactional-accounts`, { headers: headers() })
      .then((r) => setRows(r.data.accounts || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const clean = rows.filter((r) => String(r.account || '').trim());
      const res = await axios.put(`${API}/non-transactional-accounts`,
        { accounts: clean }, { headers: headers() });
      setRows(res.data.accounts || []);
      setToast?.({ type: 'success', message: 'Cuentas excluidas actualizadas' });
      onClose?.(true);
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'No se pudo guardar' });
    } finally { setBusy(false); }
  };

  const patch = (i, key, value) =>
    setRows(rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="excluded-accounts-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto">
        <h3 className="font-bold text-gray-900">Cuentas sin pagos de clientes</h3>
        <p className="text-xs text-gray-500 mt-1 mb-4">
          Los créditos que caigan en estas cuentas y no emparejen con una orden no entran a la
          bandeja ni suman al total. Los pagos que sí tengan orden se siguen acreditando normal.
        </p>

        {loading ? (
          <p className="text-sm text-gray-400">Cargando…</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex gap-2 items-start" data-testid={`excluded-row-${i}`}>
                <input value={r.account || ''} onChange={(e) => patch(i, 'account', e.target.value)}
                  placeholder="Número de cuenta" data-testid={`excluded-account-${i}`}
                  className="border rounded-lg px-3 py-2 text-xs font-mono w-56" />
                <input value={r.label || ''} onChange={(e) => patch(i, 'label', e.target.value)}
                  placeholder="A quién pertenece" data-testid={`excluded-label-${i}`}
                  className="border rounded-lg px-3 py-2 text-xs flex-1" />
                <button onClick={() => setRows(rows.filter((_, idx) => idx !== i))}
                  data-testid={`excluded-remove-${i}`}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button onClick={() => setRows([...rows, { account: '', label: '' }])}
              data-testid="excluded-add"
              className="text-xs flex items-center gap-1 px-3 py-2 border rounded-lg hover:bg-gray-50">
              <Plus className="w-3 h-3" /> Agregar cuenta
            </button>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={() => onClose?.(false)} data-testid="excluded-cancel"
            className="px-4 py-2 text-xs border rounded-lg hover:bg-gray-50">Cancelar</button>
          <button onClick={save} disabled={busy || loading} data-testid="excluded-save"
            className="px-4 py-2 text-xs rounded-lg bg-gray-900 text-white hover:bg-black disabled:opacity-40">
            {busy ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
